// Scroll-position policy for ChatRoom, extracted as pure functions so the
// branching can be unit-tested without a DOM / jsdom scroll emulation.

// Distance from true bottom (in px) still considered "exactly at the bottom" —
// absorbs sub-pixel rounding and scroll-snap quantization so the auto-scroll
// engagement and the jump-to-bottom button don't flicker near the edge. Kept
// tight on purpose: it gates the streaming auto-follow, so a deliberate scroll-up
// during an AI answer should disengage promptly rather than fight the reader.
export const SCROLL_BOTTOM_THRESHOLD_PX = 10;

// Wider "basically caught up" band used only for discrete message appends (the
// live-chat / human-handover path). When the reader is within this distance of
// the bottom, a newly arrived message follows them down like a normal messenger;
// beyond it they're treated as reading scrollback and the message is surfaced via
// the unread indicator instead of yanking the view. Intentionally NOT used for
// streaming, so the AI assistant's mid-stream reading behaviour is unchanged.
export const NEAR_BOTTOM_THRESHOLD_PX = 120;

// Structural shape of the bits of an HTMLElement we measure. Lets tests pass
// plain objects and keeps these helpers DOM-free.
export interface ScrollMetrics {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
}

const distanceFromBottom = (m: ScrollMetrics): number => m.scrollHeight - (m.scrollTop + m.clientHeight);

export const isScrolledToBottom = (m: ScrollMetrics): boolean => distanceFromBottom(m) <= SCROLL_BOTTOM_THRESHOLD_PX;

export const isNearBottom = (m: ScrollMetrics): boolean => distanceFromBottom(m) <= NEAR_BOTTOM_THRESHOLD_PX;

// What to do when the conversation's message count changes.
//  - 'ignore':  not an appended-message event we should react to (chat switch,
//               initial 0 -> N populate, no growth, or the AI is mid-stream and
//               the streaming auto-follow owns the scroll).
//  - 'follow':  reader is caught up — bring them down to the new message.
//  - 'unread':  reader is up in the scrollback — don't move them; surface the
//               new message(s) via the unread indicator instead.
export type AppendOutcome = 'ignore' | 'follow' | 'unread';

export const classifyAppend = (params: {
    prevCount: number;
    messageCount: number;
    chatChanged: boolean;
    // true while the AI is streaming (botResponseStatus truthy). Streaming appends
    // are owned by the dedicated streaming effect, never this discrete-append path.
    isStreaming: boolean;
    nearBottom: boolean;
}): AppendOutcome => {
    const { prevCount, messageCount, chatChanged, isStreaming, nearBottom } = params;
    if (chatChanged) return 'ignore';
    // prevCount > 0 skips the initial 0 -> N populate (async conversation load),
    // which is the chat-switch effect's job, not an appended-message event.
    if (!(prevCount > 0 && messageCount > prevCount)) return 'ignore';
    if (isStreaming) return 'ignore';
    return nearBottom ? 'follow' : 'unread';
};
